import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NullableRecord } from '@app/types';
import { SharedMasterDataUserPropertiesSchema } from '@libs/shared/domain/master-data/user/shared-master-data-user.properties.schema';
import { HttpService } from '@services/http.service';
import { UserRoleModel } from '@shared/models/user-role';
import { set, sortBy } from 'lodash';
import { catchError, map, Observable } from 'rxjs';

export type UserUpdateModel = NullableRecord<SharedMasterDataUserPropertiesSchema> & { username: string };
export type UserCreateModel = UserUpdateModel;

@Injectable({ providedIn: 'root' })
export class UserApiService extends HttpService {
  get(username: string): Observable<SharedMasterDataUserPropertiesSchema | undefined> {
    return this.httpClient
      .get<SharedMasterDataUserPropertiesSchema>(`${this.urlService.USERS_URL}/${username}`)
      .pipe(catchError((response: HttpErrorResponse) => this.handleGetError(response)));
  }

  create(user: UserCreateModel): Observable<UserCreateModel> {
    user = this.patchModel(user);
    return this.httpClient.post<UserCreateModel>(`${this.urlService.USERS_URL}/${user.username}`, user);
  }

  update(user: UserUpdateModel): Observable<UserUpdateModel> {
    return this.httpClient.patch<UserUpdateModel>(`${this.urlService.USERS_URL}/${user.username}`, user);
  }

  userExists(usernameOrEmail: string): Observable<boolean> {
    return this.get(usernameOrEmail).pipe(map(user => !!user));
  }

  reInvite(username: string): Observable<{ id: string }> {
    return this.httpClient.post<{ id: string }>(`${this.urlService.AUTH_SERVICE_URL}/re-invite`, { username });
  }

  getRoles() {
    return this.httpClient.get<UserRoleModel[]>(this.urlService.USERS_ROLES_URL).pipe(map(roles => sortBy(roles, 'displayName')));
  }

  private patchModel(user: UserUpdateModel): UserUpdateModel {
    if (user.username) {
      set(user, 'username', user.username.trim().toLowerCase());
    }
    if (user.email) {
      set(user, 'email', user.email.trim().toLowerCase());
    }
    return user;
  }
}
